import PropTypes from "prop-types";
import { Box, Card, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SHADOW_MD,
} from "../../../../theme/tokens";

function formatRelativeTime(isoString) {
  if (!isoString) return "";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(isoString).toLocaleDateString();
}

export default function ArtifactCard({ project, onSelect }) {
  return (
    <Card
      variant="outlined"
      onClick={() => onSelect(project)}
      sx={{
        p: 2,
        height: "100%",
        minHeight: 180,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderColor: HAIRLINE,
        transition:
          "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: SHADOW_MD,
          borderColor: HAIRLINE_STRONG,
        },
        "&:hover .artifact-arrow": { opacity: 1, transform: "translateX(0)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: `${RADIUS.sm}px`,
            display: "grid",
            placeItems: "center",
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
          }}
        >
          <SourceOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography
          sx={{
            fontSize: FONT.lg,
            fontWeight: 600,
            color: INK,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={project.name}
        >
          {project.name}
        </Typography>
        <ArrowForwardIcon
          className="artifact-arrow"
          sx={{
            fontSize: 18,
            color: ACCENT,
            opacity: 0,
            flexShrink: 0,
            transform: "translateX(-4px)",
            transition: "opacity 0.16s ease, transform 0.16s ease",
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: FONT.sm,
          color: project.description ? INK_SECONDARY : INK_MUTED,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "2.6em",
        }}
      >
        {project.description || "No description provided."}
      </Typography>

      <Typography
        sx={{ fontSize: FONT.xs, color: INK_MUTED, mt: "auto", pt: 0.5 }}
      >
        Updated {formatRelativeTime(project.last_activity_at)}
      </Typography>
    </Card>
  );
}

ArtifactCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    last_activity_at: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};
